import React, { useContext, useState, useEffect } from 'react';
import { 
  getAuth, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile,
  updateEmail as updateEmailAuth,
  updatePassword as updatePasswordAuth,
  deleteUser
} from 'firebase/auth';
import { 
  doc, 
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc
} from 'firebase/firestore'; // Move Firestore imports here
import { db } from '../firebase'; // Import db from your firebase config

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userClaims, setUserClaims] = useState({});
  const auth = getAuth();

  async function signup(email, password, displayName) {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update profile with display name
      await updateProfile(user, { displayName });

      // Create user document in Firestore
      const userDoc = doc(db, 'users', user.uid);
      await setDoc(userDoc, {
        email,
        displayName,
        createdAt: new Date(),
      });

      return user;
    } catch (error) {
      throw error;
    }
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    return signOut(auth);
  }

  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email);
  }

  function updateEmail(email) {
    return updateEmailAuth(currentUser, email);
  }

  function updatePassword(password) {
    return updatePasswordAuth(currentUser, password);
  }

  // Function to delete the current user's account and data
  async function deleteCurrentUser() {
    if (!currentUser) {
      throw new Error("No user currently signed in.");
    }

    const userId = currentUser.uid;
    console.log(`Attempting to delete data and account for user: ${userId}`);

    try {
      // --- Delete associated Firestore data --- 
      // Add or remove lines here based on your actual Firestore structure
      
      const collectionsToDelete = [
        'users', 
        'userSettings', 
        'userMemorized', 
        'userNotes', 
        'dhikrProgress', 
        'userGoals' // Note: This won't delete subcollections if they exist
      ];
      
      const deletePromises = collectionsToDelete.map(collectionName => {
        console.log(`Preparing to delete document in ${collectionName} for user ${userId}`);
        const docRef = doc(db, collectionName, userId);
        return deleteDoc(docRef).catch(err => {
          // Log error but don't necessarily stop other deletions
          console.error(`Failed to delete document in ${collectionName} for user ${userId}:`, err);
        });
      });

      // Wait for all delete operations to settle
      await Promise.allSettled(deletePromises);
      console.log(`Finished attempting Firestore data deletion for user: ${userId}`);
      
      // --- Now delete the Firebase Auth user --- 
      console.log(`Attempting to delete Firebase Auth user: ${userId}`);
      await deleteUser(currentUser);
      setCurrentUser(null); // Clear user state immediately
      console.log(`User ${userId} Auth record deleted successfully.`);
      
    } catch (error) {
      console.error(`Error during deletion process for user ${userId}:`, error);
      // Handle specific errors like 'auth/requires-recent-login'
      if (error.code === 'auth/requires-recent-login') {
        throw new Error("Please sign out and sign back in before deleting your account.");
      }
      // Rethrow other critical errors (like issues during auth deletion itself)
      throw new Error("An error occurred while deleting the account."); 
    }
  }

  // Check if user is a super admin
  async function checkSuperAdminStatus(user) {
    if (!user) {
      setIsSuperAdmin(false);
      setUserClaims({});
      return;
    }

    try {
      // Get ID token result which includes custom claims
      const idTokenResult = await user.getIdTokenResult();
      const claims = idTokenResult.claims;

      setUserClaims(claims);
      setIsSuperAdmin(claims.superAdmin === true);

      console.log('User claims:', claims);
      console.log('Is super admin:', claims.superAdmin === true);
    } catch (error) {
      console.error('Error checking super admin status:', error);
      setIsSuperAdmin(false);
      setUserClaims({});
    }
  }

  // Function to refresh token (important after claims change)
  async function refreshUserToken() {
    if (currentUser) {
      await currentUser.getIdToken(true); // Force refresh
      await checkSuperAdminStatus(currentUser);
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      // Check super admin status
      await checkSuperAdminStatus(user);

      setLoading(false);
    });

    return unsubscribe;
  }, [auth]);

  const value = {
    currentUser,
    isSuperAdmin,
    userClaims,
    refreshUserToken,
    signup,
    login,
    logout,
    resetPassword,
    updateEmail,
    updatePassword,
    deleteCurrentUser
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}